import React, { useState, useRef, useEffect } from "react";
import { FiCamera, FiTrash2 } from "react-icons/fi";
import { FaUser } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import clsx from "clsx";
import toast from "react-hot-toast";

import ResponsiveWrapper from "@/components/common/ResponsiveWrapper";
import useLogout from "@/hooks/useLogout";
import type { UpdateUserProfilePayload, UserNotificationSettingUpdate } from "@/services/UserService";

const BIO_MAX_LENGTH = 200;

const normalizeExternalUrl = (value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
};

type EditProfileUser = {
    name: string;
    bio: string;
    emailAddress?: string;
    handle?: string;
    avatarUrl?: string;
    phone?: string;
    company?: string;
    websiteUrl?: string;
    socialLinks?: {
        instagram?: string;
        tiktok?: string;
        youtube?: string;
        facebook?: string;
        twitter?: string;
    };
    showScoreLeaderboard?: boolean;
    profilePrivate?: boolean;
    notificationSettings?: UserNotificationSettingUpdate[];
};

type EditProfileFormState = {
    bio: string;
    firstName: string;
    lastName: string;
    nickname: string;
    phone: string;
    company: string;
    websiteUrl: string;
    instagram: string;
    tiktok: string;
    youtube: string;
    facebook: string;
    xUrl: string;
    showScoreOnScoreboard: boolean;
    isPrivateProfile: boolean;
    emailFrequency: "daily" | "weekly" | "never";
    pushFrequency: "daily" | "weekly" | "never";
    commentNotificationsEnabled: boolean;
};

const buildInitialFormState = (profile: EditProfileUser): EditProfileFormState => ({
    bio: profile.bio ?? "",
    firstName: profile.name.split(" ")[0] ?? "",
    lastName: profile.name.split(" ").slice(1).join(" "),
    nickname: (profile.handle ?? "").replace(/^@/, ""),
    phone: profile.phone ?? "",
    company: profile.company ?? "",
    websiteUrl: profile.websiteUrl ?? "",
    instagram: profile.socialLinks?.instagram ?? "",
    tiktok: profile.socialLinks?.tiktok ?? "",
    youtube: profile.socialLinks?.youtube ?? "",
    facebook: profile.socialLinks?.facebook ?? "",
    xUrl: profile.socialLinks?.twitter ?? "",
    showScoreOnScoreboard: profile.showScoreLeaderboard ?? true,
    isPrivateProfile: profile.profilePrivate ?? false,
    emailFrequency:
        profile.notificationSettings?.find((s) => s.messageType === "new_listings_available")?.emailFrequency ??
        "daily",
    pushFrequency:
        profile.notificationSettings?.find((s) => s.messageType === "new_listings_available")?.pushFrequency ??
        "daily",
    commentNotificationsEnabled:
        profile.notificationSettings?.find((s) => s.messageType === "comment_reply")?.emailFrequency !== "never",
});

export type EditProfileModalProps = {
    open: boolean;
    onClose: () => void;
    profile: EditProfileUser;
    onSave: (payload: UpdateUserProfilePayload, profileImageFile?: File | null) => Promise<void>;
};

export function EditProfileModalBase({
    open,
    onClose,
    profile,
    onSave,
    showContactAndSocial,
}: EditProfileModalProps & { showContactAndSocial: boolean }) {
    const [avatarUrl, setAvatarUrl] = useState<string | undefined>(profile.avatarUrl);
    const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
    const [form, setForm] = useState<EditProfileFormState>(() => buildInitialFormState(profile));
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const wasOpenRef = useRef(open);
    const { logoutUser } = useLogout();

    // Initialize modal state only when transitioning from closed -> open.
    // This prevents wiping in-progress edits when parent re-renders with a new profile object identity.
    useEffect(() => {
        const isOpening = open && !wasOpenRef.current;
        if (isOpening) {
            setAvatarUrl(profile.avatarUrl);
            setSelectedAvatarFile(null);
            setForm(buildInitialFormState(profile));
            setSaveError(null);
        }
        wasOpenRef.current = open;
    }, [open, profile]);

    // Revoke any blob URLs created for previews to avoid leaks
    useEffect(() => {
        let currentUrl: string | undefined;
        if (avatarUrl && avatarUrl.startsWith("blob:")) {
            currentUrl = avatarUrl;
        }
        return () => {
            if (currentUrl) {
                URL.revokeObjectURL(currentUrl);
            }
        };
    }, [avatarUrl]);

    const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setSelectedAvatarFile(file);
        const url = URL.createObjectURL(file);
        setAvatarUrl((prev) => {
            if (prev && prev.startsWith("blob:")) {
                URL.revokeObjectURL(prev);
            }
            return url;
        });
    };

    const handleSave = async () => {
        if (isSaving) return;
        const displayName = `${form.firstName} ${form.lastName}`.trim();
        const trimmedNickname = form.nickname.trim().replace(/^@/, "");
        const basePayload = {
            ...(displayName ? { displayName } : {}),
            ...(trimmedNickname ? { nickname: trimmedNickname } : {}),
            bio: form.bio.trim(),
            showScoreLeaderboard: form.showScoreOnScoreboard,
            profilePrivate: form.isPrivateProfile,
            notificationSettings: [
                {
                    messageType: "new_listings_available" as const,
                    emailFrequency: form.emailFrequency,
                    pushFrequency: form.pushFrequency,
                },
                {
                    messageType: "new_user_tips" as const,
                    emailFrequency: form.emailFrequency,
                    pushFrequency: form.pushFrequency,
                },
                {
                    messageType: "user_awards" as const,
                    emailFrequency: form.emailFrequency,
                    pushFrequency: form.pushFrequency,
                },
                {
                    messageType: "user_referral" as const,
                    emailFrequency: form.emailFrequency,
                    pushFrequency: form.pushFrequency,
                },
                {
                    messageType: "new_daily_puzzle" as const,
                    emailFrequency: form.emailFrequency,
                    pushFrequency: form.pushFrequency,
                },
                {
                    messageType: "comment_reply" as const,
                    emailFrequency: (form.commentNotificationsEnabled ? "daily" : "never") as "daily" | "never",
                    pushFrequency: "never" as const,
                },
            ],
        };

        const payload = showContactAndSocial
            ? {
                  ...basePayload,
                  agentData: {
                      brokerage: form.company.trim() || undefined,
                      phone: form.phone.trim() || undefined,
                      website: normalizeExternalUrl(form.websiteUrl),
                      instagram: normalizeExternalUrl(form.instagram),
                      tiktok: normalizeExternalUrl(form.tiktok),
                      facebook: normalizeExternalUrl(form.facebook),
                      youtube: normalizeExternalUrl(form.youtube),
                      x: normalizeExternalUrl(form.xUrl),
                  },
              }
            : basePayload;

        setSaveError(null);
        setIsSaving(true);
        try {
            await onSave(payload, selectedAvatarFile);
            toast.success("Profile updated successfully.");
        } catch (error: any) {
            const message = error?.response?.data?.message;
            setSaveError(message || "Couldn't save profile changes. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };
    return (
        <ResponsiveWrapper
            show={open}
            onHide={onClose}
            className="tw-flex tw-items-center tw-justify-center tw-px-0 md:tw-px-4"
            contentClassName="!tw-bg-transparent !tw-border-0 !tw-shadow-none"
            drawerHeight="90vh"
        >
            <div className="tw-max-h-[90vh] tw-flex tw-flex-col">
                {/* Sticky header */}
                <div className="tw-bg-[var(--meteorite)] tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-center tw-justify-between tw-px-5 tw-pt-3.5 tw-pb-3 tw-rounded-t-[16px]">
                    <h2 className="tw-text-white tw-text-[20px] tw-font-merriweather tw-font-bold">Edit Profile</h2>
                    <button
                        type="button"
                        aria-label="Close edit profile"
                        className="tw-flex tw-items-center tw-bg-transparent tw-justify-center tw-rounded-full tw-text-white hover:tw-bg-white/10"
                        onClick={onClose}
                    >
                        <IoMdClose className="tw-text-xl" />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="tw-bg-white tw-flex-1 tw-overflow-y-auto tw-pr-1">
                    <div className="tw-px-6 tw-pt-4 tw-pb-3 tw-space-y-6">
                        <section className="tw-space-y-4 tw-flex tw-flex-col tw-items-center">
                        <h3 className="tw-text-[15px] tw-font-merriweather tw-font-bold tw-w-full tw-text-left">
                            Profile Information
                        </h3>

                        {/* Profile photo */}
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                            <span className="tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A]">Profile Photo</span>
                            <div className="tw-relative tw-inline-flex tw-items-center tw-justify-center">
                                <button
                                    type="button"
                                    className="tw-relative tw-group tw-w-[96px] tw-h-[96px] tw-rounded-full tw-overflow-hidden tw-bg-[#f3f3f3] tw-border-2 tw-border-[#E5E7EB] tw-flex tw-items-center tw-justify-center tw-shadow-md tw-cursor-pointer"
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    {avatarUrl ? (
                                        <img
                                            src={avatarUrl}
                                            alt={profile.name}
                                            className="tw-w-full tw-h-full tw-object-cover"
                                        />
                                    ) : (
                                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-[#9CA3AF]">
                                            <FaUser className="tw-text-2xl" />
                                        </div>
                                    )}
                                    <div className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-black/40 tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity">
                                        <FiCamera className="tw-text-white tw-text-xl" />
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className="tw-absolute tw-bottom-0 tw-right-0 tw-z-20 tw-translate-x-[1px] tw-translate-y-[1px] tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#ff4b4b] tw-flex tw-items-center tw-justify-center tw-shadow-md"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                            setAvatarUrl(undefined);
                                            setSelectedAvatarFile(null);
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = "";
                                            }
                                    }}
                                >
                                    <FiTrash2 className="tw-text-white tw-text-sm" />
                                </button>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="tw-hidden"
                                onChange={handleAvatarFileChange}
                            />
                            <span className="tw-text-[11px] tw-text-[var(--nobel-gray)]">Click to change photo</span>
                        </div>

                        {/* Name, email, and nickname fields */}
                        <div className="tw-w-full tw-space-y-2 tw-mt-2">
                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                                <div>
                                    <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                        First Name
                                    </label>
                                    <input
                                        className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                        value={form.firstName}
                                        onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                        Last Name
                                    </label>
                                    <input
                                        className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                        value={form.lastName}
                                        onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                    Email
                                </label>
                                <input
                                    className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-bg-[#F4F4F6] tw-text-[#6B7280]"
                                    value={profile.emailAddress ?? ""}
                                    disabled
                                    readOnly
                                />
                                <p className="tw-mt-1 tw-text-[11px] tw-text-[var(--nobel-gray)]">
                                    <a
                                        href="/contact"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="!tw-text-[#7c3aed] tw-no-underline hover:!tw-text-[#6d28d9]"
                                    >
                                        Contact support
                                    </a>{" "}
                                    to change your email
                                </p>
                            </div>

                            <div>
                                <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                    Nickname
                                </label>
                                <input
                                    className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                    value={form.nickname}
                                    onChange={(e) => setForm((prev) => ({ ...prev, nickname: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="tw-w-full">
                            <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">Bio</label>
                            <textarea
                                className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-min-h-[80px]"
                                maxLength={BIO_MAX_LENGTH}
                                value={form.bio}
                                onChange={(e) => {
                                    const next = e.target.value.slice(0, BIO_MAX_LENGTH);
                                    setForm((prev) => ({ ...prev, bio: next }));
                                }}
                            />
                            <div className="tw-mt-1 tw-text-[11px] tw-text-[var(--nobel-gray)]">
                                {form.bio.length} / {BIO_MAX_LENGTH} characters
                            </div>
                        </div>
                    </section>

                    {showContactAndSocial && (
                        <>
                            <section className="tw-space-y-3">
                                <h3 className="tw-text-[15px] tw-font-merriweather tw-font-bold">Contact Information</h3>
                                <div className="tw-space-y-3 tw-w-full">
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            Phone
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.phone}
                                            onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            Brokerage
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.company}
                                            onChange={(e) => setForm((prev) => ({ ...prev, company: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            Website
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.websiteUrl}
                                            onChange={(e) => setForm((prev) => ({ ...prev, websiteUrl: e.target.value }))}
                                        />
                                    </div>
                                </div>
                            </section>

                            <section className="tw-space-y-3">
                                <h3 className="tw-text-[15px] tw-font-merriweather tw-font-bold">Social Media</h3>
                                <div className="tw-space-y-3 tw-w-full">
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            Instagram
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.instagram}
                                            onChange={(e) => setForm((prev) => ({ ...prev, instagram: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            TikTok
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.tiktok}
                                            onChange={(e) => setForm((prev) => ({ ...prev, tiktok: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            YouTube
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.youtube}
                                            onChange={(e) => setForm((prev) => ({ ...prev, youtube: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            Facebook
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.facebook}
                                            onChange={(e) => setForm((prev) => ({ ...prev, facebook: e.target.value }))}
                                        />
                                    </div>
                                    <div>
                                        <label className="tw-block tw-text-[13px] tw-font-semibold tw-text-[#4A4A4A] tw-mb-1">
                                            X
                                        </label>
                                        <input
                                            className="tw-w-full tw-border tw-border-[var(--grey)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                                            value={form.xUrl}
                                            onChange={(e) => setForm((prev) => ({ ...prev, xUrl: e.target.value }))}
                                        />
                                    </div>
                                </div>
                            </section>
                        </>
                    )}

                        <hr className="tw-w-full tw-border-t tw-border-gray tw-mt-2 tw-mb-4" />

                        {/* Privacy Settings */}
                        <section className="tw-space-y-3 tw-w-full">
                        <h3 className="tw-text-[15px] tw-font-merriweather tw-font-bold">Privacy Settings</h3>

                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-[#f7f7f7] tw-px-3 tw-py-3">
                            <div>
                                <p className="tw-text-sm tw-font-semibold">Show Score on Scoreboard</p>
                                <p className="tw-text-xs tw-text-[var(--nobel-gray)]">
                                    Your scores will be visible to other players
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={form.showScoreOnScoreboard}
                                onClick={() =>
                                    setForm((prev) => ({ ...prev, showScoreOnScoreboard: !prev.showScoreOnScoreboard }))
                                }
                                className={clsx(
                                    "tw-w-10 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-p-0.5 tw-transition-colors",
                                    form.showScoreOnScoreboard ? "tw-bg-[#6C26C4]" : "tw-bg-[#E0E0E0]",
                                )}
                            >
                                <span
                                    className={clsx(
                                        "tw-w-5 tw-h-5 tw-rounded-full tw-bg-white tw-shadow-sm tw-transform tw-transition-transform",
                                        form.showScoreOnScoreboard ? "tw-translate-x-4" : "tw-translate-x-0",
                                    )}
                                />
                            </button>
                        </div>

                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-[#f7f7f7] tw-px-3 tw-py-3">
                            <div>
                                <p className="tw-text-sm tw-font-semibold">Private Profile</p>
                                <p className="tw-text-xs tw-text-[var(--nobel-gray)]">
                                    Only followers can see your activity
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={form.isPrivateProfile}
                                onClick={() => setForm((prev) => ({ ...prev, isPrivateProfile: !prev.isPrivateProfile }))}
                                className={clsx(
                                    "tw-w-10 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-p-0.5 tw-transition-colors",
                                    form.isPrivateProfile ? "tw-bg-[#6C26C4]" : "tw-bg-[#E0E0E0]",
                                )}
                            >
                                <span
                                    className={clsx(
                                        "tw-w-5 tw-h-5 tw-rounded-full tw-bg-white tw-shadow-sm tw-transform tw-transition-transform",
                                        form.isPrivateProfile ? "tw-translate-x-4" : "tw-translate-x-0",
                                    )}
                                />
                            </button>
                        </div>
                        </section>

                        {/* Notifications */}
                        <section className="tw-space-y-4 tw-w-full">
                        <h3 className="tw-text-[15px] tw-font-merriweather tw-font-bold">Notifications</h3>

                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-[#f7f7f7] tw-px-3 tw-py-3">
                            <div>
                                <p className="tw-text-sm tw-font-semibold">Comment Notifications</p>
                                <p className="tw-text-xs tw-text-[var(--nobel-gray)]">
                                    We&apos;ll email you when someone replies to your comment
                                </p>
                            </div>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={form.commentNotificationsEnabled}
                                onClick={() =>
                                    setForm((prev) => ({
                                        ...prev,
                                        commentNotificationsEnabled: !prev.commentNotificationsEnabled,
                                    }))
                                }
                                className={clsx(
                                    "tw-w-10 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-p-0.5 tw-transition-colors",
                                    form.commentNotificationsEnabled ? "tw-bg-[#6C26C4]" : "tw-bg-[#E0E0E0]",
                                )}
                            >
                                <span
                                    className={clsx(
                                        "tw-w-5 tw-h-5 tw-rounded-full tw-bg-white tw-shadow-sm tw-transform tw-transition-transform",
                                        form.commentNotificationsEnabled ? "tw-translate-x-4" : "tw-translate-x-0",
                                    )}
                                />
                            </button>
                        </div>

                        <div className="tw-space-y-2">
                            <p className="tw-text-sm tw-font-semibold">Email Notifications</p>
                            <div className="tw-flex tw-gap-2">
                                {(["daily", "weekly", "never"] as const).map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setForm((prev) => ({ ...prev, emailFrequency: value }))}
                                        className={clsx(
                                            "tw-flex-1 tw-rounded-lg tw-py-2 tw-text-sm tw-font-semibold tw-transition-colors",
                                            form.emailFrequency === value
                                                ? "tw-bg-[#6C26C4] tw-text-white"
                                                : "tw-bg-[#F3F3F3] tw-text-[#666666]",
                                        )}
                                    >
                                        {value === "daily"
                                            ? "Daily"
                                            : value === "weekly"
                                              ? "Weekly"
                                              : "Never"}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="tw-space-y-2">
                            <p className="tw-text-sm tw-font-semibold">Push Notifications</p>
                            <div className="tw-flex tw-gap-2">
                                {(["daily", "weekly", "never"] as const).map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setForm((prev) => ({ ...prev, pushFrequency: value }))}
                                        className={clsx(
                                            "tw-flex-1 tw-rounded-lg tw-py-2 tw-text-sm tw-font-semibold tw-transition-colors",
                                            form.pushFrequency === value
                                                ? "tw-bg-[#6C26C4] tw-text-white"
                                                : "tw-bg-[#F3F3F3] tw-text-[#666666]",
                                        )}
                                    >
                                        {value === "daily"
                                            ? "Daily"
                                            : value === "weekly"
                                              ? "Weekly"
                                              : "Never"}
                                    </button>
                                ))}
                            </div>
                        </div>

                            <button
                                type="button"
                                onClick={logoutUser}
                                className="tw-w-full tw-rounded-lg tw-bg-[#F7F7F7] tw-text-sm tw-font-semibold tw-text-black tw-py-2.5 tw-mt-2 tw-border tw-border-[#FBC9C3] hover:tw-bg-[#FFF5F4] tw-transition-colors"
                            >
                                Log Out
                            </button>
                        </section>
                    </div>
                </div>

                {/* Sticky footer actions */}
                <div className="tw-border-t tw-border-[var(--grey)] tw-pt-3 tw-pb-4 tw-px-6 tw-bg-white tw-sticky tw-bottom-0 tw-z-10">
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-w-full tw-mx-auto">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSaving}
                            className="tw-flex-1 tw-rounded-lg tw-w-full tw-py-2.5 tw-text-sm tw-font-semibold tw-bg-[#F4F4F6] tw-text-[#111827] tw-border tw-border-[#E5E7EB]"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="tw-flex-1 tw-rounded-lg tw-w-full tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-bg-[var(--orange)] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                        >
                            {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                    {saveError && <p className="tw-text-red-500 tw-text-xs tw-mt-2">{saveError}</p>}
                </div>
            </div>
        </ResponsiveWrapper>
    );
}

export function AgentEditProfileModal(props: EditProfileModalProps) {
    return <EditProfileModalBase {...props} showContactAndSocial />;
}

export function UserEditProfileModal(props: EditProfileModalProps) {
    return <EditProfileModalBase {...props} showContactAndSocial={false} />;
}

